import dayjs, { Dayjs } from 'dayjs';
import { Period, TimePeriod } from '../../types/date';
import { formatDate, formatDateShortWithYear, formatDateWithoutYear } from './format';

type TranslateFn = (key: string) => string;

export const QUARTER_IN_MONTH = 3;
const CYCLE_IN_MONTHS = 4;

function getCycleStart(date: Dayjs): Dayjs {
  const cycleStartMonth = Math.floor(date.month() / CYCLE_IN_MONTHS) * CYCLE_IN_MONTHS;

  return date.month(cycleStartMonth).startOf('month');
}

export const getPeriodUntilDate = (date: Dayjs, period: TimePeriod, dateInclusive = false): Period => {
  const endOfPeriod = dateInclusive ? date.add(1, 'days') : date;
  let beginnOfPeriod: Dayjs;
  switch (period) {
    case TimePeriod.MONTH: {
      beginnOfPeriod = endOfPeriod.subtract(1, 'month').add(1, 'day');
      break;
    }
    case TimePeriod.WEEK: {
      beginnOfPeriod = endOfPeriod.subtract(1, 'week').add(1, 'day');
      break;
    }
    case TimePeriod.QUARTER: {
      beginnOfPeriod = endOfPeriod.subtract(QUARTER_IN_MONTH, 'month').add(1, 'day');
      break;
    }
    case TimePeriod.CYCLE: {
      beginnOfPeriod = getCycleStart(endOfPeriod);
      break;
    }
    case TimePeriod.YTD: {
      beginnOfPeriod = endOfPeriod.startOf('year');
      break;
    }
    case TimePeriod.YEAR: {
      beginnOfPeriod = endOfPeriod.subtract(1, 'year').add(1, 'day');
      break;
    }
    case TimePeriod.TWO_YEARS: {
      beginnOfPeriod = endOfPeriod.subtract(2, 'year').add(1, 'day');
      break;
    }
    default: {
      beginnOfPeriod = endOfPeriod.clone();
    }
  }
  return { beginn: beginnOfPeriod, end: endOfPeriod, period };
};

export const getPreviousCustomPeriod = (period: Period) => {
  const diff = dayjs(period.end).diff(dayjs(period.beginn), 'day');
  const startDate = period.beginn.subtract(diff + 1, 'day');
  return { ...period, end: period.beginn.subtract(1, 'day'), beginn: dayjs(startDate) };
};

export const getPreviousPeriod = (period: Period): Period => {
  if (period.period === TimePeriod.YTD) {
    const beginn = period.beginn.subtract(1, 'year');
    const end = period.end.subtract(1, 'year');
    return { beginn, end, period: TimePeriod.YTD };
  }

  if (period.period === TimePeriod.CUSTOM) {
    return getPreviousCustomPeriod(period);
  }
  return getPeriodUntilDate(period.beginn.subtract(1, 'day'), period.period);
};

export const formatPeriod = (period: Period): string => {
  const periodWithinOnYear = period.beginn.year() === period.end.year();
  return (
    (periodWithinOnYear ? formatDateWithoutYear(period.beginn) : formatDate(period.beginn)) +
    ' - ' +
    formatDate(period.end)
  );
};

export const formatPeriodShortWithYear = (period: Period): string => {
  return formatDateShortWithYear(period.beginn) + ' - ' + formatDateShortWithYear(period.end);
};

export const formatePreviousDateRange = (comparisonPeriod: Period): string => {
  return `vs ${formatDateShortWithYear(comparisonPeriod.beginn)} - ${formatDateShortWithYear(comparisonPeriod.end)}`;
};

export const formatPreviousDateRangeText = (comparisonPeriod: Period, t: TranslateFn): string | null => {
  switch (comparisonPeriod.period) {
    case TimePeriod.WEEK:
      return t('header.navigation.dateRange.previousWeek');
    case TimePeriod.MONTH:
      return t('header.navigation.dateRange.previousMonth');
    case TimePeriod.QUARTER:
      return t('header.navigation.dateRange.previousQuarter');
    case TimePeriod.YTD:
      return t('header.navigation.dateRange.previousYTD');
    case TimePeriod.YEAR:
      return t('header.navigation.dateRange.previousYear');
    case TimePeriod.CYCLE:
      return t('header.navigation.dateRange.previousCycle');
    case TimePeriod.CUSTOM:
      return formatePreviousDateRange(comparisonPeriod);
    default:
      return null;
  }
};

export const isSamePeriod = (firstPeriod: Period, secondPeriod: Period, unit: dayjs.OpUnitType = 'day'): boolean => {
  if (firstPeriod.period !== secondPeriod.period) {
    return false;
  }
  const isSameStart = firstPeriod.beginn.isSame(secondPeriod.beginn, unit);
  const isSameEnd = firstPeriod.end.isSame(secondPeriod.end, unit);
  return isSameStart && isSameEnd;
};
